import { useState, useRef } from "react";
import { Box, Button, Typography } from "@mui/material";
import { CloudUpload as UploadIcon } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { fontSizes, letterSpacings } from "../../../design-system/tokens";

export const FileUploadZone = ({
  onFilesSelected,
  accept = "*",
  multiple = true,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      onFilesSelected(files);
    }
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      onFilesSelected(files);
    }
    // Reset input value to allow selecting the same file again
    e.target.value = "";
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box
      data-testid="file-upload-zone"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        width: "100%",
        border: "1px dashed",
        borderColor: isDragging ? "primary.main" : "divider",
        borderRadius: 1,
        bgcolor: isDragging ? (theme) => alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity) : "background.paper",
        p: 5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        cursor: "pointer",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: "primary.main",
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
        },
      }}
      onClick={handleBrowseClick}
    >
      <UploadIcon
        sx={{
          fontSize: 80,
          color: "primary.main",
          mb: 1,
        }}
      />

      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          fontSize: fontSizes.base,
          color: "text.primary",
          textAlign: "center",
        }}
      >
        Drag & Drop to Upload File
      </Typography>

      <Typography
        variant="body2"
        sx={{
          fontSize: fontSizes.base,
          color: "text.primary",
        }}
      >
        or
      </Typography>

      <Button
        data-testid="browse-file-button"
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation();
          handleBrowseClick();
        }}
        sx={{
          px: 2,
          py: 0.75,
          fontSize: fontSizes.base,
          fontWeight: 600,
          textTransform: "none",
          letterSpacing: letterSpacings.button,
        }}
      >
        Browse File
      </Button>

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleFileInputChange}
        style={{ display: "none" }}
        data-testid="file-input"
      />
    </Box>
  );
};
